/**
 * @class
 * @function TdPrescriptionForm
 * Handles prescription form submissions to Capture Health API
 */
if (!customElements.get('td-prescription-form')) {
  customElements.define('td-prescription-form', class TdPrescriptionForm extends HTMLElement {
    constructor() {
      super();
      
      // Get elements
      this.submitButton = this.querySelector('.td-prescription-form__button');
      this.errorMessageWrapper = this.querySelector('.td-prescription-form__error-message-wrapper');
      this.errorMessage = this.querySelector('.td-prescription-form__error-message');
      this.dosageId = this.dataset.dosageId?.trim();
      this.sectionId = this.dataset.section;
      this.API_URL = 'https://popstarsmd-middleware.netlify.app/.netlify/functions/submit-prescription';
      
      // Initialize button click handler
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
      }
      
      // Listen for Shopify's built-in variant change events
      document.addEventListener('product:variant-change', this.handleVariantChange.bind(this));
    }
    
    /**
     * Handle variant changes from Shopify's system
     */
    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (variant && variant.price) {
        this.updatePriceDisplay(variant.price);
      }
    }
    
    /**
     * Update price display with Shopify's price data
     */
    updatePriceDisplay(price) {
      const priceElement = this.querySelector('.td-prescription-form__price');
      if (!priceElement) return;
      
      const formattedPrice = this.formatMoney(price);
      priceElement.textContent = formattedPrice;
    }
    
    /**
     * Format price using Shopify's money format
     */
    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }
    
    /**
     * Handle button click
     */
    onSubmitHandler(evt) {
      evt.preventDefault();
      
      const form = document.querySelector('[data-th-product-information]');
      let quantity = 6; // default
      let frequency = 30; // default

      // Get quantity and frequency from form -> might change this since its a little fragile
      if (form) {   
        const quantityElements = form.querySelector('[data-handle*="quantity"]');
        const frequencyElements = form.querySelector('[data-handle*="frequency"]');
        if (quantityElements) {
          const checkedQuantity = quantityElements.querySelector('input[type="radio"]:checked');
          if (checkedQuantity) {
            quantity = parseInt(checkedQuantity.value);
          }
        }
        if (frequencyElements) {
          const checkedFrequency = frequencyElements.querySelector('input[type="radio"]:checked');
          if (checkedFrequency) {
            frequency = parseInt(checkedFrequency.value) * 30; // Convert months to days
          }
        }
      }
    
      // Validate required data
      if (!this.dosageId) {
        this.showError('Invalid data. Please try again.');
        return;
      }
      
      // Prepare prescription data
      const prescriptionData = {
        prescription: {
          compoundDosageId: this.dosageId,
          quantity: quantity,
          frequency: frequency
        }
      };
      this.submitPrescription(prescriptionData);
    }
    
    /**
     * Submit prescription to Capture Health API
     */
    async submitPrescription(prescriptionData) {
      try {
        this.setLoadingState(true);

        const response = await fetch(this.API_URL, {
          method: 'POST',
          headers: {
            'Origin': `${window.location.origin}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(prescriptionData)
        });
        
        const responseData = await response.json();
        
        if (response.ok) {
          // Success
          this.showRedirectPopup(responseData.redirectUrl);
        } else {
          // API error
          this.showError(`API Error: ${response.status} - ${responseData.message || 'Unknown error'}`);
        }
      } catch (error) {
        this.showError('Network error occurred');
      } finally {
        this.setLoadingState(false);
      }
    }
    
    /**
     * Set loading state on button
     */
    setLoadingState(loading) {
      if (this.submitButton) {
        if (loading) {
          this.submitButton.classList.add('loading');
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
        }
      }
    }
    
    /**
     * Show error message
     */
    showError(message) {
      if (this.errorMessageWrapper && this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessageWrapper.hidden = false;
        
        // Hide error after 5 seconds
        setTimeout(() => {
          this.errorMessageWrapper.hidden = true;
        }, 5000);
      }
    }
    
    /**
     * Show redirect popup with iframe
     */
    showRedirectPopup(redirectUrl) {
      const popup = document.createElement('div');
      popup.className = 'td-prescription-popup';
      popup.innerHTML = `
        <div class="td-prescription-popup__overlay">
          <div class="td-prescription-popup__content">
            <div class="td-prescription-popup__header">
              <h3>Complete Your Prescription</h3>
              <button class="td-prescription-popup__close" aria-label="Close popup">×</button>
            </div>
            <div class="td-prescription-popup__body">
              <iframe 
                src="${redirectUrl}" 
                width="100%" 
                height="100%" 
                frameborder="0"
                title="Prescription Intake Form"
              ></iframe>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(popup);
      document.body.classList.add('popup-open');
      const closeBtn = popup.querySelector('.td-prescription-popup__close');
      closeBtn.addEventListener('click', () => {
        this.closePopup(popup);
      });
      
      const overlay = popup.querySelector('.td-prescription-popup__overlay');
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closePopup(popup);
        }
      });
      
      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && document.body.contains(popup)) {
          this.closePopup(popup);
        }
      });
    }
    
    /**
     * Close popup and restore body scroll
     */
    closePopup(popup) {
      if (popup && document.body.contains(popup)) {
        document.body.removeChild(popup);
        document.body.classList.remove('popup-open');
      }
    }
  });
}
